/**
 * Persist the last map view (lon/lat/zoom) in localStorage so leaving and
 * coming back — or switching between the home map and Explore — restores the
 * same location + zoom, without needing it in the URL.
 *
 * Shareable `?lat=&lon=&z=` links still win over the stored view (see
 * initialMapView): an explicit link expresses intent, the store is just memory.
 *
 * localStorage (not a cookie): it's client-only state, so there's no reason to
 * ship it to the server on every request.
 */

import { readCoordFromUrl, readZoomFromUrl } from './coord-url'

const KEY = 'gb.mapView'

export interface MapView {
  lon: number
  lat: number
  zoom: number
}

/** Read the stored view, or null when absent / malformed / storage blocked. */
export function readStoredMapView(): MapView | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(KEY)
    if (!raw) return null
    const v = JSON.parse(raw) as Partial<MapView>
    if (
      typeof v.lon === 'number' && Number.isFinite(v.lon) &&
      typeof v.lat === 'number' && Number.isFinite(v.lat) &&
      typeof v.zoom === 'number' && Number.isFinite(v.zoom)
    ) {
      return { lon: v.lon, lat: v.lat, zoom: v.zoom }
    }
  } catch {
    /* private mode / disabled storage / bad JSON — treat as no memory */
  }
  return null
}

/** Save the current view. No-ops on non-finite values or when storage throws. */
export function saveMapView(lon: number, lat: number, zoom: number): void {
  if (typeof window === 'undefined') return
  if (!Number.isFinite(lon) || !Number.isFinite(lat) || !Number.isFinite(zoom)) return
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ lon, lat, zoom }))
  } catch {
    /* ignore quota / disabled storage */
  }
}

/**
 * Resolve the initial view for a map page, with priority:
 *   URL params (a shared link) → stored last view → the provided defaults.
 */
export function initialMapView(
  defaultCenter: [number, number],
  defaultZoom: number,
): { center: [number, number]; zoom: number } {
  const urlCoord = readCoordFromUrl()
  const urlZoom = readZoomFromUrl()
  const stored = readStoredMapView()
  const center: [number, number] =
    urlCoord ?? (stored ? [stored.lon, stored.lat] : defaultCenter)
  const zoom = urlZoom ?? stored?.zoom ?? defaultZoom
  return { center, zoom }
}
